import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import usePageTitle from '../hooks/usePageTitle';
import './PipelineCreator.css';

const PipelineCreator = () => {
  usePageTitle('New Pipeline');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!formData.name.trim()) {
      setError('Pipeline name is required');
      setLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.post('/etl/pipelines/', formData);
      navigate(`/pipelines/${response.data.id}`);
    } catch (err) {
      console.error('Failed to create pipeline:', err);
      setError(err.response?.data?.detail || err.response?.data?.name?.[0] || 'Failed to create pipeline');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pipeline-creator-container">
      <div className="pipeline-creator-header">
        <button onClick={() => navigate('/pipelines')} className="btn-back">
          &larr; Back
        </button>
        <div>
          <h1>Create Pipeline</h1>
          <p className="header-subtitle">Group related ETL flows together</p>
        </div>
      </div>

      <div className="pipeline-creator-content">
        <form onSubmit={handleSubmit} className="pipeline-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="name">Pipeline Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Partners Pipeline"
              required
            />
            <span className="hint">A unique name for this pipeline</span>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe what this pipeline does and which flows it contains..."
              rows={4}
            />
          </div>

          <div className="form-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/pipelines')}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Creating...' : 'Create Pipeline'}
            </button>
          </div>
        </form>

        <div className="info-panel">
          <h3>What is a Pipeline?</h3>
          <p>
            A Pipeline groups multiple ETL flows together so you can run them in sequence.
            This is useful for organizing related data flows, such as:
          </p>
          <ul>
            <li><strong>Bronze + Silver flows:</strong> Extract data from Odoo, then transform it</li>
            <li><strong>Parent + Child flows:</strong> Load orders first, then order lines</li>
            <li><strong>Multi-layer pipelines:</strong> Chain Bronze → Silver → Gold transformations</li>
          </ul>
          <div className="tip">
            <strong>Tip:</strong> After creating the pipeline, you can add flows as steps
            and arrange them in the execution order you want.
          </div>
        </div>
      </div>
    </div>
  );
};

export default PipelineCreator;
